import { FC, forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import axios from 'axios';
import cx from 'classnames';
import OutsideClickHandler from 'react-outside-click-handler';
//@ts-ignore
import Select, { components } from 'react-select';

import Icon from 'components/shared/Icon';
import ArrowUp from 'components/shared/Icon/icons/ArrowUp';
import Close from 'components/shared/Icon/icons/Close';
import Plus from 'components/shared/Icon/icons/Plus';
import MenuActionsHelper from 'components/shared/MenuActionsHelper';
import { ContentIndicator } from 'components/shared/Select/components/ContentIndicator';
import { useEscAction } from 'hooks/useEscAction';
import { debounce } from 'utils/debounce';
import noop from 'utils/noop';
import { isNotDefined } from 'utils/ramda';

import { FormFieldWrapper } from '..';
import s from './index.module.css';
import { getSelectCustomStyles } from './styles';
import { IBaseSelectProps, ISelectOption, ISecondarySelectOption, BaseSelectOption } from './types';

const defaultOptions: Array<ISelectOption> | Array<ISecondarySelectOption> = [];

export const BaseSelect: FC<IBaseSelectProps> = ({
  loadOptions,
  onChange,
  placeholder,
  helpText,
  errorMessage,
  defaultOption,
  optionActionIcon,
  onOptionActionClick,
  onOptionCreation,
  optionClassName,
  onMenuOpen,
  readOnly,
  isClearable,
  isCreatable = false,
  options: incomeOptions = defaultOptions,
  value,
  isMulti,
  debounceTime = 1000,
}) => {
  const [options, setOptions] = useState<ISelectOption[] | ISecondarySelectOption[]>([]);
  const [selectedValue, setSelectedValue] = useState<BaseSelectOption | BaseSelectOption[] | null>(
    defaultOption || null,
  );

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // @ts-ignore
  const [inputValue, setInputValue] = useState<string>(selectedValue?.name ?? '');
  const selectRef = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEscAction(() => handleCloseMenu());

  //@ts-ignore
  const handleSelectChange = (option, { action }) => {
    if (action === 'select-option') {
      const configParams = option.fsCode && { fsCode: option.fsCode };
      const newOptionName = option.name ? option.name : option.label;
      const newOptionId = option.id ? option.id : option.value;

      if (loadOptions) {
        onChange(newOptionName, newOptionId, configParams);
      } else {
        onChange(newOptionId, newOptionName);
      }

      const newOption = { name: newOptionName, id: newOptionId };

      if (isMulti) {
        handleInputValue(newOptionName ?? '');
        setSelectedValue(option);

        handleCloseMenu();
      } else {
        handleInputValue(newOptionName ?? '');
        setSelectedValue(newOption);
        handleCloseMenu();
      }

      if (!loadOptions) {
        setOptions(incomeOptions);
      }
    }

    if (action === 'clear') {
      onChange(null, null);
      handleInputValue('');
      setSelectedValue(null);
      handleCloseMenu();
    }

    setIsMenuOpen(false);
  };

  const handleOptionCreation = () => {
    const newOption = { id: null, name: inputValue, isCustom: true };
    onOptionCreation && onOptionCreation(newOption);
    onChange(newOption.name, newOption.id, { isCustom: true });
    setSelectedValue(newOption);
    handleCloseMenu();
  };

  const handleInputValue = (value: string) => {
    if (loadOptions) {
      setIsLoading(true);
      setIsMenuOpen(true);
    } else {
      setOptions(
        // @ts-ignore
        incomeOptions?.filter((option: ISecondarySelectOption) =>
          option.label.toLowerCase().includes(value.toLowerCase()),
        ),
      );
    }
    setInputValue(value);
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
    handleInputBlur();
  };

  const handleInputBlur = () => {
    inputRef.current?.blur();
  };

  const triggerInputFocus = () => {
    inputRef.current?.focus();
  };

  const handleDropdownIndicatorClick = () => {
    setIsMenuOpen(!isMenuOpen);

    if (isMenuOpen) {
      handleInputBlur();
    } else {
      triggerInputFocus();
    }
  };

  //@ts-ignore
  const ControlMemoized = useCallback((props) => <Control label={placeholder} {...props} />, []);

  const MenuMemoized = useCallback(
    //@ts-ignore
    (props) => (
      <OutsideClickHandler onOutsideClick={handleCloseMenu} useCapture={false}>
        <Menu isCreatable={isCreatable} onOptionCreation={handleOptionCreation} {...props} />
      </OutsideClickHandler>
    ),
    [handleOptionCreation],
  );

  const OptionMemoized = useCallback(
    //@ts-ignore
    (props) => (
      <Option
        onActionClick={onOptionActionClick}
        actionIcon={optionActionIcon}
        triggerBlur={handleInputBlur}
        className={optionClassName}
        {...props}
      />
    ),
    [],
  );

  const InputMemoized = useCallback(
    //@ts-ignore
    (props) => <Input ref={inputRef} {...props} />,
    [],
  );

  const fetchOptionsDebounced = useCallback(
    //@ts-ignore
    debounce(async (q, additionalQueryParams) => {
      try {
        //@ts-ignore
        const options: ISelectOption[] = await loadOptions(q, additionalQueryParams);
        options && setOptions(options);
      } catch (err) {
        if (!(err instanceof axios.Cancel)) {
          throw err;
        }
      } finally {
        setIsLoading(false);
      }
    }, debounceTime),
    [loadOptions],
  );

  useEffect(() => {
    if (isMenuOpen && loadOptions) {
      setIsLoading(true);
      fetchOptionsDebounced(inputValue);
    }
  }, [isMenuOpen, inputValue]);

  useEffect(() => {
    if (defaultOption === null) {
      setSelectedValue(null);
      setInputValue('');
      return;
    }

    if (isNotDefined(defaultOption) && isNotDefined(value)) {
      setSelectedValue(null);
      setInputValue('');
    }
  }, [defaultOption, value]);

  useEffect(() => {
    if (!loadOptions) {
      setOptions(incomeOptions);
      setSelectedValue(defaultOption || null);
    }
  }, [loadOptions, incomeOptions]);

  useEffect(() => {
    if (incomeOptions && value) {
      if (isMulti) {
        const initialOptions = incomeOptions as ISecondarySelectOption[];
        setSelectedValue(initialOptions);
      } else {
        const initialOption = (incomeOptions as ISecondarySelectOption[]).find((option) => option?.value === value);
        if (initialOption) {
          setSelectedValue({ id: initialOption.value, name: initialOption.label });
          setInputValue(initialOption.label);
        }
      }
    }
  }, [incomeOptions, value]);

  return (
    <FormFieldWrapper errorMessage={errorMessage} helpText={helpText}>
      <Select
        ref={selectRef}
        onKeyDown={(event: KeyboardEvent) => {
          if (event.key === 'Enter' && inputValue && isCreatable) {
            event.preventDefault();
            handleOptionCreation();
          }
        }}
        value={selectedValue}
        isDisabled={readOnly}
        isLoading={isLoading && isMenuOpen}
        placeholder=""
        components={{
          Menu: MenuMemoized,
          Input: InputMemoized,
          Control: ControlMemoized,
          NoOptionsMessage: NoOptionsMessage,
          LoadingMessage: LoadingMessage,
          Option: onOptionActionClick ? OptionMemoized : components.Option,
          // FIXME: add type after react-select library updating to version 5
          DropdownIndicator: (props: any) => (
            <DropdownIndicator {...props} onClick={handleDropdownIndicatorClick} isRotated={isMenuOpen} />
          ),
          ClearIndicator,
          MultiValueRemove: (props: any) => {
            if (!isMulti) return null;
            return <MultiValueRemove {...props} />;
          },
          MultiValueLabel: (props: any) => {
            if (!isMulti) return null;
            return <MultiValueLabel {...props} />;
          },
        }}
        menuIsOpen={isMenuOpen}
        // @ts-ignore
        inputValue={isMenuOpen ? inputValue : selectedValue?.name || ''}
        isClearable={isClearable}
        filterOption={() => true}
        onChange={handleSelectChange}
        controlShouldRenderValue={false}
        onInputChange={(value: string, { action }: { action: string }) => {
          if (action === 'input-change') {
            handleInputValue(value);
          }
        }}
        onFocus={() => {
          setIsMenuOpen(true);
        }}
        onMenuOpen={onMenuOpen}
        options={options}
        styles={getSelectCustomStyles(errorMessage)}
        isMulti={isMulti}
      />
    </FormFieldWrapper>
  );
};

//@ts-ignore
const MultiValueLabel = ({ data, ...props }) => {
  const { label, count } = data;
  const countPart = count && count > 1 ? ` x ${count}` : '';

  return <components.MultiValueLabel {...props}>{label + countPart}</components.MultiValueLabel>;
};

//@ts-ignore
const MultiValueRemove = (props) => (
  <components.MultiValueRemove {...props}>
    <Icon component={Close} width="1.4rem" height="1.4rem" className={s.removeIcon} />
  </components.MultiValueRemove>
);

//@ts-ignore
const Option = ({ children, data, onActionClick, actionIcon, triggerBlur, ...props }) => {
  return (
    <components.Option {...props} data={data}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {data?.showContentIndicator && <ContentIndicator isSharedPage={data?.shared} />}
        {children}
        {data?.subtitle && <div className={s.additionalInfo}>{data.subtitle}</div>}
      </div>
      {onActionClick && (
        <Icon
          component={actionIcon}
          width="1.8rem"
          height="1.8rem"
          onClick={(event) => {
            event.stopPropagation();
            triggerBlur();
            onActionClick(data.id);
          }}
        />
      )}
    </components.Option>
  );
};

//@ts-ignore
const Menu = ({ children, anchorRef, selectProps, onOptionCreation, isCreatable, ...props }) => {
  const { inputValue } = selectProps;

  return (
    <components.Menu innerRef={anchorRef} {...props}>
      {children}
      {isCreatable ? (
        <>
          <div className={s.createButton} onClick={inputValue ? onOptionCreation : noop}>
            <Icon component={Plus} fill="#212121" />
            <span className={s.createButtonText}>Create</span>
            {inputValue && <span className={s.createButtonValueText}>{` "${inputValue}"`}</span>}
          </div>
          {/* @ts-ignore */}
          <MenuActionsHelper isNew />
        </>
      ) : null}
    </components.Menu>
  );
};

//@ts-ignore
const Control = ({ children, selectProps, label, ...props }) => {
  const shouldFloatLabel = selectProps.menuIsOpen || selectProps.value?.name;

  return (
    <components.Control {...props}>
      {children}
      {label && <div className={cx(s.placeholder, { [s.filled]: shouldFloatLabel })}>{label}</div>}
    </components.Control>
  );
};

//@ts-ignore
const DropdownIndicator = ({ onClick, isRotated, ...props }) => (
  <components.DropdownIndicator {...props}>
    <Icon
      component={ArrowUp}
      className={cx(s.selectIcon, { [s['selectIcon-noRotated']]: isRotated })}
      onClick={onClick}
      data-testid="baseSelectDropdownIndicator"
    />
  </components.DropdownIndicator>
);

//@ts-ignore
const ClearIndicator = (props) => (
  <components.ClearIndicator {...props}>
    <Icon component={Close} data-testid="baseSelectClearIndicator" />
  </components.ClearIndicator>
);

//@ts-ignore
const NoOptionsMessage = (props) => (
  <components.NoOptionsMessage {...props}>
    <div className={s.noOptionsMessage}>No options found</div>
  </components.NoOptionsMessage>
);

//@ts-ignore
const LoadingMessage = (props) => (
  <components.LoadingMessage {...props}>
    <div className={s.noOptionsMessage}>Loading...</div>
  </components.LoadingMessage>
);

const Input = forwardRef<HTMLInputElement>(
  //@ts-ignore
  ({ id, isDisabled, onBlur, onChange, onFocus, tabIndex, value, type }, ref) => {
    return (
      <input
        id={id}
        ref={ref}
        aria-autocomplete="list"
        autoCapitalize="none"
        autoComplete="off"
        autoCorrect="off"
        type={type}
        disabled={isDisabled}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        tabIndex={tabIndex}
        value={value}
        style={{
          border: 'none',
          padding: '17px 0 0 2px',
          width: '100%',
          color: '#3e3e40',
        }}
        data-testid="baseSelectInput"
      />
    );
  },
);
